// Static configuration + runtime env resolution for a broker-consumer worker.
// Extracted from apps/retells/worker/config.ts and apps/corpus/worker/config.ts,
// which were the same file with different hostnames, cookie names and brand
// strings. Everything that differed is now `ConsumerAppConfig`, supplied by the
// app; everything that did not is a single definition here.
//
// The worker holds ZERO auth secrets: the broker's `/auth/exchange` takes no
// client credential (the one-time code is the bearer) and JWKS verification
// uses public keys. Every value here is plain text and safe to commit.

import { BROKER_AUDIENCE } from "@bindery/topology";
import type { PeerSet } from "@bindery/topology";
import {
  DEFAULT_LANES,
  SESSION_LANES,
  isSessionLane,
  type SessionLane,
} from "./verify";

/**
 * What one consumer app tells this package about itself.
 *
 * `hosts` and `defaultPeers` MUST come from `@bindery/topology`
 * (`hostsOf(app)`, `peersFor(target)` / `SOURCE_PEERS`) rather than be written
 * out here or in the app: the topology is the one place the deployment is
 * described, and a hostname re-spelled by hand is a hostname that can drift
 * from the wrangler routes that actually serve it.
 */
export interface ConsumerAppConfig<L extends SessionLane = "user_session"> {
  /** The `service` discriminator on every log line, e.g. `"retells"`. */
  readonly service: string;
  /** Human-readable product name, used only by the diagnostic page. */
  readonly brand: string;
  /**
   * Cookie-name prefix, e.g. `"retells"` → `retells-session`,
   * `retells-authtry`, `retells-authstate`. Each consumer names its own:
   * cookies ignore ports, so on the `localhost` dev peer the broker and every
   * consumer would otherwise clobber one shared host-only name.
   */
  readonly cookiePrefix: string;
  /**
   * Every host this consumer is served on, across every deploy target — the
   * app passes `hostsOf(app)`. This one list is the host gate (guardrail 2's
   * default-deny), the absolute-`returnTo` allowlist, and what the app's
   * `scripts/wrangler.test.ts` checks its routes against. An entry with no
   * route dead-ends sign-in; a route with no entry bounces the user off the
   * host they started on. Both failures are silent at build time.
   */
  readonly hosts: readonly string[];
  /**
   * Hosts admitted ONLY over plain HTTP. `localhost` is a name an attacker can
   * put in a `Host` header, not one we own, so it may never qualify as an auth
   * host against the deployed (HTTPS-only) worker.
   */
  readonly devHosts?: readonly string[];
  /**
   * Where a request on an UNKNOWN host is funnelled — a `*.workers.dev`
   * preview, a stray alias. The app passes its canonical host for the target
   * it deploys, so such a request lands somewhere real instead of trying to
   * hold a session it cannot keep.
   */
  readonly defaultHost: string;
  /** What the `AUTH_*` vars fall back to when unset. */
  readonly defaultPeers: PeerSet;
  /** Expected `aud` on the broker JWT. Defaults to the broker's audience. */
  readonly audience?: string;
  /**
   * The broker session types this app accepts — at `/auth/callback` (the
   * exchange and the verify) and at `requireSession`. Omitted, it is
   * `["user_session"]`, which is every consumer that predates the reader
   * lane, unchanged. An app that serves readers with no account of their own
   * opts in with `["user_session", "reader_session"]` (or `reader_session`
   * alone), and then MUST branch on the `lane` its sessions come back with —
   * a `reader_session` is obtainable with no credential at all, and its `sub`
   * is a readerId, not a WorkOS id. See the README's "Session lanes".
   *
   * Checked when the consumer is built: an empty list or an unknown lane
   * throws, because a consumer that accepts nothing is a deploy that signs
   * nobody in, with no error anywhere but the logs.
   */
  readonly lanes?: readonly L[];
  /**
   * Opt in to the broker's reader-linking entry. On, `/auth/sign-in` forwards
   * a valid `readerId` and `includeReaders=true` to `/redirect`, and
   * `/auth/sign-in-light` and `/auth/manage` start the light Google lane and
   * `/manage` with the same sign-in CSRF state. Off (the default), those two
   * routes are 404 and `/auth/sign-in` forwards `returnTo` alone. See the
   * README's "Reader-linking entry".
   */
  readonly readerLinking?: boolean;
  /**
   * The path prefix every route this consumer serves sits under, and the
   * `Path` its cookies are scoped to. Defaults to {@link DEFAULT_BASE_PATH}.
   * An app that shares its hosts with another worker owning `/auth*` mounts
   * the group under its own subtree instead, e.g. `"/enroll/auth"`. Checked
   * when the consumer is built: a leading `/`, no trailing `/`, plain path
   * segments only.
   */
  readonly basePath?: string;
  /**
   * Where the consumer sends a user it has nowhere better to send: the
   * `returnTo` fallback, the unhandled-error redirect, the diagnostic page's
   * reload link. Defaults to `/`. Checked like `basePath`, except that `/`
   * itself is allowed.
   */
  readonly homePath?: string;
}

/** A consumer config of any lane set — what the lane-agnostic helpers take. */
export type AnyConsumerAppConfig = ConsumerAppConfig<SessionLane>;

/**
 * The lanes `app` accepts, defaulted and checked. Throws a plain `Error` (at
 * `createAuthConsumer` time, i.e. module scope in the Worker) on an empty
 * list or a value outside {@link SESSION_LANES} — the latter reachable only
 * from untyped JS or a cast, which is exactly where nothing else would catch
 * it.
 */
export function lanesOf(app: AnyConsumerAppConfig): readonly SessionLane[] {
  const lanes = app.lanes ?? DEFAULT_LANES;
  if (lanes.length === 0) {
    throw new Error(
      `${app.service}: \`lanes\` is empty, so this consumer would accept no ` +
        `session at all. Omit it for user_session only.`,
    );
  }
  for (const lane of lanes) {
    if (!isSessionLane(lane)) {
      throw new Error(
        `${app.service}: unknown session lane ${JSON.stringify(lane)}; ` +
          `expected one of ${SESSION_LANES.join(", ")}.`,
      );
    }
  }
  return lanes;
}

/** Cookies are scoped to the base path — the JWT never rides a static-asset
 *  or `/api/*` request; the SPA fetches it from `/auth/token` and presents it
 *  as a Bearer header. */
export const DEFAULT_BASE_PATH = "/auth";
/** The default cookie `Path`, which is the default base path. */
export const COOKIE_PATH = DEFAULT_BASE_PATH;
export const DEFAULT_HOME_PATH = "/";

// One or more plain segments. Excluding `?`, `#`, whitespace, backslashes and
// empty segments keeps a configured path from ever reading as a query, a
// protocol-relative URL or a header split once it reaches a `Location`.
const MOUNT_PATH_PATTERN = /^(?:\/[A-Za-z0-9._~!$&'()*+,;=:@%-]+)+$/;

function checkMountPath(
  app: AnyConsumerAppConfig,
  field: "basePath" | "homePath",
  value: string,
  allowRoot: boolean,
): string {
  if (allowRoot && value === "/") return value;
  const dotted =
    value.includes("..") || value.split("/").some((part) => part === ".");
  if (!MOUNT_PATH_PATTERN.test(value) || dotted) {
    throw new Error(
      `${app.service}: \`${field}\` ${JSON.stringify(value)} must start ` +
        `with "/", must not end with "/", and may hold only plain path ` +
        `segments (no "?", "#", whitespace, "." or "..").`,
    );
  }
  return value;
}

/**
 * The route prefix and cookie `Path` for `app`, defaulted and checked.
 * Throws a plain `Error` at `createAuthConsumer` time, like {@link lanesOf}.
 */
export function basePathOf(app: AnyConsumerAppConfig): string {
  return checkMountPath(
    app,
    "basePath",
    app.basePath ?? DEFAULT_BASE_PATH,
    false,
  );
}

/** Where `app` sends a user with nowhere better to go, defaulted and checked. */
export function homePathOf(app: AnyConsumerAppConfig): string {
  return checkMountPath(
    app,
    "homePath",
    app.homePath ?? DEFAULT_HOME_PATH,
    true,
  );
}

/** Cloudflare `vars` bound to the worker. All optional — the defaults win. */
export interface AuthEnv {
  AUTH_BROKER_ORIGIN?: string;
  AUTH_EXPECTED_ISSUER?: string;
  AUTH_EXPECTED_AUDIENCE?: string;
  AUTH_EXCHANGE_AUDIENCE?: string;
}

export interface AuthConfig {
  /** Broker origin — exchange + sign-out + JWKS base. */
  brokerOrigin: string;
  /** Expected `iss` on the broker JWT (a bare host; see topology `PeerSet`). */
  expectedIssuer: string;
  /** Expected `aud` on the broker JWT (the broker's `DEFAULT_AUDIENCE`). */
  expectedAudience: string;
  /**
   * Optional override for the hostname binding presented to `/auth/exchange`.
   * Normally UNSET — the audience is the (host-gated) request host, because
   * the code was minted against whichever host started sign-in. Set it only to
   * pin one audience (e.g. the dev peer, whose request host is `localhost`,
   * which no broker mints codes for).
   */
  exchangeAudiencePin?: string;
}

/**
 * A deploy whose `AUTH_*` vars cannot describe one broker. Thrown at request
 * time by `resolveConfigFor` — the vars arrive from the Cloudflare binding, so
 * there is no earlier moment to check them at, and every caller here turns it
 * into a refusal rather than letting it escape.
 *
 * `reason` is the case-distinguishing token for the log line; the message
 * carries the operator-facing detail. Both are plain configuration text, never
 * a credential — this worker holds no secrets.
 */
export class AuthConfigError extends Error {
  readonly reason: "half_set_peer" | "blank_peer_var";

  constructor(reason: "half_set_peer" | "blank_peer_var", message: string) {
    super(message);
    this.name = "AuthConfigError";
    this.reason = reason;
  }
}

/** Nonce lifetime; the broker's own signed-state TTL is 10 minutes, so a
 *  sign-in that outlived this could not have completed there either. */
export const STATE_TTL_SECONDS = 600;
/** Entropy in the nonce. 16 bytes → 22 base64url chars. */
export const STATE_BYTES = 16;
/** Failed round-trips before the circuit breaker serves a diagnostic page. */
export const MAX_TRIES = 5;
/** Lifetime of the circuit-breaker attempt counter. */
export const TRY_TTL_SECONDS = 120;
/**
 * The cookie never outlives the token it stores; this caps it at the
 * broker's longest token, a sliding reader_session's 30 days. A 7-day
 * user_session's cookie still ends at its own `exp`.
 */
export const MAX_SESSION_SECONDS = 30 * 24 * 60 * 60;

/** The default `devHosts` when an app names none. */
export const DEFAULT_DEV_HOSTS: readonly string[] = ["localhost"];

function devHostsOf(app: AnyConsumerAppConfig): readonly string[] {
  return app.devHosts ?? DEFAULT_DEV_HOSTS;
}

/**
 * Resolve the effective config for a request on `hostname`.
 *
 * Vars win when set; `app.defaultPeers` otherwise. There is deliberately NO
 * host→broker inference: corpus once carried a `brokerForHost` rule so that
 * `*.fugl.dev` spoke to the fugl broker and everything else to production,
 * because the PAGE picked a broker independently of the worker and the two
 * disagreed on `localhost`. The page no longer picks — `/auth/sign-in` is a
 * worker route now, so there is exactly one resolver — and which peers a
 * deploy may name is the topology's decision (`allowedPeersFor`), enforced by
 * each app's wrangler test. Switching brokers is a redeploy with different
 * vars, which signs every session out; it is not a per-request branch.
 *
 * `hostname` is retained in the signature because it is the request's
 * (already host-gated) host and every caller has it: it is what a future
 * per-host rule would need, and it keeps `requireSession` and `handleAuth`
 * resolving through one call shape.
 *
 * THROWS `AuthConfigError` when the vars name half a peer set. The origin and
 * the issuer are one decision — where the reader is sent, and whose signature
 * is then trusted — and they fell back independently, so setting only
 * `AUTH_EXPECTED_ISSUER` left the worker redirecting to the DEFAULT broker
 * while demanding the var's `iss`. `wrangler-invariants` catches that in a
 * committed `wrangler.jsonc`, but a var can also arrive from the dashboard, a
 * `.dev.vars`, or a `--var` flag, none of which it sees. Refusing here is
 * strictly better than the alternative failure: a mixed pair fails as an
 * opaque `verification_failed` on every sign-in, or — when both brokers happen
 * to share a key — quietly succeeds against an issuer nobody meant to trust.
 */
export function resolveConfigFor(
  app: AnyConsumerAppConfig,
  env: AuthEnv,
  hostname: string,
): AuthConfig {
  void hostname;
  // Trimmed, because a var carrying a stray newline is a var an operator
  // believes they set. A present-but-blank one is refused rather than treated
  // as unset: it is unambiguously a mistake, and silently falling back would
  // hide it behind a working sign-in against the wrong broker.
  const brokerOrigin = env.AUTH_BROKER_ORIGIN?.trim();
  const expectedIssuer = env.AUTH_EXPECTED_ISSUER?.trim();
  if (brokerOrigin === "" || expectedIssuer === "") {
    throw new AuthConfigError(
      "blank_peer_var",
      `${app.service}: AUTH_BROKER_ORIGIN / AUTH_EXPECTED_ISSUER is set to an ` +
        `empty value. Set both to a real peer set, or unset both to use ` +
        `${app.defaultPeers.authOrigin}.`,
    );
  }
  if ((brokerOrigin === undefined) !== (expectedIssuer === undefined)) {
    throw new AuthConfigError(
      "half_set_peer",
      `${app.service}: AUTH_BROKER_ORIGIN and AUTH_EXPECTED_ISSUER are one ` +
        `peer set and must be set together — got ` +
        `AUTH_BROKER_ORIGIN=${JSON.stringify(brokerOrigin ?? null)}, ` +
        `AUTH_EXPECTED_ISSUER=${JSON.stringify(expectedIssuer ?? null)}. ` +
        `Unset, they fall back to ${app.defaultPeers.authOrigin} / ` +
        `${app.defaultPeers.authIssuer}, so half-setting them sends the reader ` +
        `to one broker and verifies the token against another.`,
    );
  }
  return {
    brokerOrigin: brokerOrigin ?? app.defaultPeers.authOrigin,
    expectedIssuer: expectedIssuer ?? app.defaultPeers.authIssuer,
    expectedAudience:
      env.AUTH_EXPECTED_AUDIENCE ?? app.audience ?? BROKER_AUDIENCE,
    exchangeAudiencePin: env.AUTH_EXCHANGE_AUDIENCE,
  };
}

/**
 * The audience to claim the one-time code with. Guardrail 2 still holds: this
 * is NOT the raw request `Host`. The caller must have already gated `hostname`
 * through `isAuthHostFor`, so a spoofed or misrouted host can only ever yield
 * an audience from the app's fixed host list — never an arbitrary one.
 */
export function exchangeAudienceFor(
  config: AuthConfig,
  hostname: string,
): string {
  return config.exchangeAudiencePin ?? hostname;
}

/**
 * Whether this consumer serves auth (and may hold a session cookie) on
 * `hostname`.
 *
 * `secure` is the handler's `url.protocol === "https:"` flag and defaults to
 * `true` — fail-closed, so a one-argument call answers the deployed question.
 * Over HTTPS only the real hosts qualify, which is what keeps a request
 * claiming `Host: localhost` from being treated as a session-holding auth host
 * against the deployed worker.
 */
export function isAuthHostFor(
  app: AnyConsumerAppConfig,
  hostname: string,
  secure = true,
): boolean {
  const wanted = hostname.toLowerCase();
  if (app.hosts.some((host) => host.toLowerCase() === wanted)) return true;
  return (
    !secure && devHostsOf(app).some((host) => host.toLowerCase() === wanted)
  );
}

/** Where a request on a host we do not serve auth on is funnelled. */
export function defaultOriginFor(app: AnyConsumerAppConfig): string {
  return `https://${app.defaultHost}`;
}
